import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FileText, AlertTriangle, CheckCircle, Activity, Calendar } from 'lucide-react';
import { getSharedRecord } from '@/services/api';

function SharedRecordPage() {
  const { token } = useParams();
  const [record, setRecord] = useState(null);
  const [expiresAt, setExpiresAt] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(function() {
    loadSharedRecord();
  }, [token]);

  async function loadSharedRecord() {
    setLoading(true);
    setError('');
    try {
      const response = await getSharedRecord(token);
      setRecord(response.record);
      setExpiresAt(response.expires_at);
    } catch (err) {
      if (err?.response?.status === 410) {
        setError('This share link has expired.');
      } else {
        setError('Unable to load this shared record.');
      }
    } finally {
      setLoading(false);
    }
  }

  function getRecordDate() {
    if (!record) return '';
    if (record.test_date) return record.test_date;
    if (record.created_at) return record.created_at.split('T')[0];
    return '';
  }

  function getMetrics() {
    if (!record?.analysis?.metrics) return [];
    return record.analysis.metrics;
  }

  function getInsights() {
    if (!record?.analysis?.insights) return [];
    return record.analysis.insights;
  }

  function getRecommendations() {
    if (!record?.analysis?.recommendations) return [];
    return record.analysis.recommendations;
  }

  function getAbnormalFindings() {
    if (!record?.analysis?.abnormal_findings) return [];
    return record.analysis.abnormal_findings;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50" data-testid="shared-record-page">
      <div className="max-w-3xl mx-auto px-4 py-10">
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <FileText className="w-7 h-7 text-blue-900" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Shared Health Record
          </h1>
          <p className="text-slate-600 mt-2">Review the shared medical data securely.</p>
        </div>

        {loading && (
          <Card className="bg-white border-slate-200 shadow-sm" data-testid="shared-record-loading">
            <CardContent className="py-12 text-center text-slate-500">Loading shared record...</CardContent>
          </Card>
        )}

        {!loading && error && (
          <Card className="bg-white border-slate-200 shadow-sm" data-testid="shared-record-error">
            <CardContent className="py-12 text-center">
              <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
              <p className="text-slate-900 font-medium">{error}</p>
              <p className="text-sm text-slate-500 mt-2">Please request a new share link from the record owner.</p>
              <Link to="/login" data-testid="shared-record-login-link">
                <Button className="mt-5 bg-blue-900 hover:bg-blue-800 text-white" data-testid="shared-record-login-btn">
                  Go to Login
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {!loading && !error && record && (
          <div className="space-y-6">
            <Card className="bg-white border-slate-200 shadow-sm" data-testid="shared-record-summary">
              <CardHeader>
                <CardTitle className="text-xl text-slate-900" data-testid="shared-record-title">
                  {record.title}
                </CardTitle>
                <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500" data-testid="shared-record-meta">
                  <span className="inline-flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {getRecordDate()}
                  </span>
                  <span className="px-2 py-0.5 bg-slate-100 rounded-full text-xs font-medium text-slate-600" data-testid="shared-record-type">
                    {record.record_type}
                  </span>
                  {expiresAt && (
                    <span className="text-xs text-blue-700" data-testid="shared-record-expiry">
                      Expires: {new Date(expiresAt).toLocaleString()}
                    </span>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {record.notes && (
                  <div className="mb-4">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Notes</p>
                    <p className="text-slate-700 mt-1" data-testid="shared-record-notes">{record.notes}</p>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Activity className="w-4 h-4" />
                  AI analysis and insights below
                </div>
              </CardContent>
            </Card>

            {getMetrics().length > 0 && (
              <Card className="bg-white border-slate-200 shadow-sm" data-testid="shared-record-metrics">
                <CardHeader>
                  <CardTitle className="text-lg">Extracted Metrics</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {getMetrics().map(function(metric, index) {
                    const isHigh = metric.status === 'high' || metric.status === 'concerning';
                    const isLow = metric.status === 'low';
                    return (
                      <div
                        key={index}
                        className={isHigh ? "p-3 rounded-lg border bg-red-50 border-red-200" : isLow ? "p-3 rounded-lg border bg-amber-50 border-amber-200" : "p-3 rounded-lg border bg-slate-50 border-slate-200"}
                        data-testid={`shared-metric-${index}`}
                      >
                        <p className="text-sm text-slate-600">{metric.name}</p>
                        <p className="text-lg font-semibold text-slate-900">{metric.value} {metric.unit}</p>
                        {metric.normal_range && (
                          <p className="text-xs text-slate-500">Normal: {metric.normal_range}</p>
                        )}
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            )}

            {getAbnormalFindings().length > 0 && (
              <Card className="bg-white border-slate-200 shadow-sm" data-testid="shared-record-alerts">
                <CardHeader>
                  <CardTitle className="text-lg text-red-700">Abnormal Findings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {getAbnormalFindings().map(function(finding, index) {
                    return (
                      <div key={index} className="flex items-start gap-2 p-3 bg-red-50 border border-red-100 rounded-lg" data-testid={`shared-alert-${index}`}>
                        <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
                        <p className="text-slate-800">{finding}</p>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            )}

            {getInsights().length > 0 && (
              <Card className="bg-white border-slate-200 shadow-sm" data-testid="shared-record-insights">
                <CardHeader>
                  <CardTitle className="text-lg">AI Insights</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {getInsights().map(function(insight, index) {
                    return (
                      <div key={index} className="p-3 bg-sky-50 rounded-lg border-l-4 border-sky-500" data-testid={`shared-insight-${index}`}>
                        <p className="text-slate-700">{insight}</p>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            )}

            {getRecommendations().length > 0 && (
              <Card className="bg-white border-slate-200 shadow-sm" data-testid="shared-record-recommendations">
                <CardHeader>
                  <CardTitle className="text-lg">Recommendations</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {getRecommendations().map(function(rec, index) {
                    return (
                      <div key={index} className="flex items-start gap-2" data-testid={`shared-recommendation-${index}`}>
                        <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                        <p className="text-slate-700">{rec}</p>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default SharedRecordPage;